import {
  AppChannel,
  PaymentInitiation,
  PaymentMethod,
  PaymentProvider,
  PaymentProviderId,
  PaymentRequest,
  PaymentReturn,
  PaymentVerification,
  ProviderCapabilities,
} from '../types';

/**
 * ─── MpesaProvider (squelette, en attente de documentation) ─────────────────
 *
 * Deux moyens de paiement prévus :
 *   - PaymentMethod.MOBILE_PUSH : push USSD/STK envoyé sur le téléphone,
 *     l'utilisateur valide avec son PIN M-Pesa → `initiate` retournera
 *     { kind: 'push_sent', … } et le front affichera l'écran d'attente
 *     avec polling de `verify`.
 *   - PaymentMethod.CARD : carte Visa M-Pesa. Selon la doc, soit une page
 *     hébergée (kind: 'redirect'), soit un push — à trancher à réception.
 *
 * BRANCHEMENT FUTUR (à la réception de la doc M-Pesa) :
 *   1. Compléter `initiate` / `verify` avec les appels au backend.
 *   2. Confirmer les marqueurs de détection de la Super App dans
 *      services/channel.ts.
 *   3. Activer le provider dans /admin/payments.
 * Aucun autre fichier du module ne doit changer.
 */
export class MpesaProvider implements PaymentProvider {
  readonly id = PaymentProviderId.MPESA;
  readonly displayName = 'M-Pesa';
  readonly capabilities: ProviderCapabilities = {
    // Carte Visa M-Pesa (plages fournies par Vodacom) + paiement M-Pesa.
    methods: [PaymentMethod.BANK_CARD, PaymentMethod.MPESA],
    currencies: ['USD', 'CDF'],
    usesRedirect: false, // hypothèse pour le push ; à confirmer pour la carte
  };

  supportsChannel(channel: AppChannel): boolean {
    // M-Pesa est le provider de la Super App. (Il pourra aussi être proposé
    // sur le web plus tard : il suffira de retourner true inconditionnellement.)
    return channel === AppChannel.MPESA_SUPER_APP;
  }

  async initiate(_request: PaymentRequest): Promise<PaymentInitiation> {
    // TODO(doc M-Pesa): POST <api>/payments/mpesa/initiate
    //  - MOBILE_PUSH → return { kind: 'push_sent', orderNumber, instructions }
    //  - CARD        → return { kind: 'redirect', url, orderNumber }
    throw new Error(
      'MpesaProvider non branché : en attente de la documentation M-Pesa.',
    );
  }

  async verify(_orderNumber: string): Promise<PaymentVerification> {
    // TODO(doc M-Pesa): GET <api>/payments/mpesa/status/{orderNumber}
    throw new Error(
      'MpesaProvider non branché : en attente de la documentation M-Pesa.',
    );
  }

  parseReturn(params: URLSearchParams): PaymentReturn | null {
    if (params.get('provider') !== this.id) return null;
    const outcome = params.get('outcome');
    if (outcome !== 'approved' && outcome !== 'cancelled' && outcome !== 'declined') {
      return null;
    }
    return {
      outcome,
      reference: params.get('reference'),
      orderNumber: params.get('orderNumber'),
    };
  }
}
